import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore, selectMembers, selectSplits, selectExpensesByMember } from '@/utils/store';
import { useAuth } from '@/context/AuthContext';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { stopRealtimeSync } from '@/lib/sync-engine';
import { useCurrency } from '@/hooks/useCurrency';
import { Button } from '@/components/ui';
import PageHeader from '@/components/shared/PageHeader';
import AddMemberModal from '@/components/shared/AddMemberModal';
import AddSplitModal from '@/components/shared/AddSplitModal';
import CoupleConnect from './CoupleConnect';
import { Plus, Users, Trash2, Heart, ArrowRightLeft, Pencil, Unlink } from 'lucide-react';

export default function Family() {
  const { t } = useTranslation();
  const { format: formatCurrency } = useCurrency();
  const { user, refreshProfile } = useAuth();
  const members = useStore(selectMembers);
  const splits = useStore(selectSplits);
  const expensesByMember = useStore(selectExpensesByMember);
  const removeMember = useStore((s) => s.removeMember);
  const isCoupled = !!user?.coupleId;
  const [disconnecting, setDisconnecting] = useState(false);

  const [tab, setTab] = useState('members');
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [showSplitModal, setShowSplitModal] = useState(false);

  const totalMemberExpenses = Object.values(expensesByMember).reduce((s, v) => s + v, 0);

  const handleDisconnect = async () => {
    if (!isSupabaseConfigured()) return;
    setDisconnecting(true);
    try {
      const { error } = await supabase.rpc('disconnect_couple');
      if (error) throw error;
      stopRealtimeSync();
      await refreshProfile();
      useStore.setState({ _fromRemote: true, isCoupled: false });
    } catch (err) {
      console.error('Disconnect failed:', err);
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <PageHeader
        title={t('family.title')}
        hint={t('common.hints.family')}
        action={
          tab === 'members' ? (
            <Button size="sm" icon={Plus} onClick={() => setShowMemberModal(true)}>
              {t('family.addMember')}
            </Button>
          ) : (
            isCoupled && (
              <Button size="sm" icon={Plus} onClick={() => setShowSplitModal(true)}>
                {t('couple.split.addNew')}
              </Button>
            )
          )
        }
      />

      {/* Tab toggle */}
      <div className="flex gap-2 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl">
        {['members', 'couple'].map((v) => (
          <button
            key={v}
            onClick={() => setTab(v)}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === v
                ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500'
            }`}
          >
            {t(`family.tabs.${v}`)}
          </button>
        ))}
      </div>

      {/* Members tab */}
      {tab === 'members' && (
        <div className="space-y-4">
          {/* Members list */}
          {members.length === 0 ? (
            <div className="card text-center py-12">
              <Users size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
              <p className="text-sm text-gray-500">{t('family.noMembers')}</p>
            </div>
          ) : (
            <>
              {/* Expense bar */}
              {totalMemberExpenses > 0 && (
                <div className="card">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                    {t('family.expensesByMember')}
                  </h3>
                  <div className="flex rounded-xl overflow-hidden h-6">
                    {members.map((m) => {
                      const amount = expensesByMember[m.id] || 0;
                      if (amount === 0) return null;
                      const pct = (amount / totalMemberExpenses) * 100;
                      return (
                        <div
                          key={m.id}
                          className="h-full flex items-center justify-center text-[10px] font-bold text-white"
                          style={{ backgroundColor: m.color, width: `${pct}%`, minWidth: '24px' }}
                          title={`${m.name}: ${formatCurrency(amount)}`}
                        >
                          {m.emoji || m.initials}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Member cards */}
              {members.map((m) => (
                <div key={m.id} className="card flex items-center gap-3">
                  {m.photo ? (
                    <img src={m.photo} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
                  ) : m.emoji ? (
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-xl shrink-0"
                      style={{ backgroundColor: m.color + '20' }}
                    >
                      {m.emoji}
                    </div>
                  ) : (
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
                      style={{ backgroundColor: m.color }}
                    >
                      {m.initials}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 dark:text-white">{m.name}</div>
                    <div className="text-xs text-gray-500">
                      {t(`family.relationships.${m.relationship}`)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(expensesByMember[m.id] || 0)}
                    </div>
                    <div className="text-[10px] text-gray-400">{t('family.totalExpenses')}</div>
                  </div>
                  <button
                    onClick={() => { setEditMember(m); setShowMemberModal(true); }}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <Pencil size={16} className="text-gray-400" />
                  </button>
                  <button
                    onClick={() => removeMember(m.id)}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <Trash2 size={16} className="text-gray-400" />
                  </button>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* Couple tab */}
      {tab === 'couple' && (
        <div className="space-y-4">
          {!isCoupled ? (
            <CoupleConnect />
          ) : (
            <>
              {/* Shared balance */}
              <div className="card bg-gradient-to-br from-pink-500 to-rose-600 text-white border-0">
                <div className="flex items-center gap-2 text-white/80 text-sm mb-2">
                  <Heart size={16} />
                  {t('couple.sharedBalance')}
                </div>
                <div className="text-3xl font-bold mb-4">{formatCurrency(2847.50)}</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/15 rounded-xl p-3">
                    <div className="text-xs text-white/70">{t('couple.myContribution')}</div>
                    <div className="text-lg font-bold">{formatCurrency(1547.50)}</div>
                  </div>
                  <div className="bg-white/15 rounded-xl p-3">
                    <div className="text-xs text-white/70">{t('couple.partnerContribution')}</div>
                    <div className="text-lg font-bold">{formatCurrency(1300.00)}</div>
                  </div>
                </div>
              </div>

              {/* Split history */}
              <div className="card">
                <h2 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <ArrowRightLeft size={18} />
                  {t('couple.splitHistory')}
                </h2>
                <div className="space-y-3">
                  {splits.map((s) => (
                    <div key={s.id} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {s.description}
                        </div>
                        <div className="text-xs text-gray-500">
                          {t('couple.total')}: {formatCurrency(s.amount)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500">{t('couple.me')}: {formatCurrency(s.myShare)}</div>
                        <div className="text-xs text-gray-500">{t('couple.partner')}: {formatCurrency(s.partnerShare)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Disconnect */}
              <Button
                variant="ghost"
                className="w-full text-danger-600 dark:text-danger-400"
                icon={Unlink}
                onClick={handleDisconnect}
                loading={disconnecting}
              >
                {t('couple.disconnect')}
              </Button>
            </>
          )}
        </div>
      )}

      <AddMemberModal
        open={showMemberModal}
        onClose={() => { setShowMemberModal(false); setEditMember(null); }}
        member={editMember}
      />
      <AddSplitModal open={showSplitModal} onClose={() => setShowSplitModal(false)} />
    </div>
  );
}
